import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Tag, User } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';

const blogPosts = [
  {
    id: 1,
    category: 'Artificial Intelligence',
    categoryColor: '#1A3BCC',
    categoryBg: '#EEF2FF',
    title: 'How AI is Transforming Software Development in 2026',
    excerpt: 'From intelligent code completion to automated testing and deployment, explore how AI tools are reshaping the developer workflow and what it means for modern software teams.',
    author: 'QuBright Tech Team',
    date: 'August 18, 2026',
    readTime: '7 min read',
    emoji: '🤖',
    tags: ['AI', 'Development', 'Future Tech'],
    featured: true,
  },
  {
    id: 2,
    category: 'Mobile Development',
    categoryColor: '#FF7B00',
    categoryBg: '#FFF7ED',
    title: 'React Native vs Flutter in 2026: Which Should You Choose?',
    excerpt: 'An in-depth comparison of the two dominant cross-platform frameworks, covering performance benchmarks, ecosystem maturity, developer experience, and real-world use cases.',
    author: 'QuBright Tech Team',
    date: 'August 15, 2026',
    readTime: '10 min read',
    emoji: '📱',
    tags: ['React Native', 'Flutter', 'Mobile'],
    featured: false,
  },
  {
    id: 3,
    category: 'EdTech',
    categoryColor: '#10B981',
    categoryBg: '#ECFDF5',
    title: 'Why Colleges Are Failing to Prepare Students for the Tech Industry',
    excerpt: 'The gap between academic curricula and industry expectations is widening. Here\'s what institutions can do — and how programs like SDP & FDP are bridging that divide.',
    author: 'QuBright Training Team',
    date: 'August 12, 2026',
    readTime: '8 min read',
    emoji: '🎓',
    tags: ['Education', 'SDP', 'Campus'],
    featured: false,
  },
  {
    id: 4,
    category: 'Cloud & DevOps',
    categoryColor: '#0EA5E9',
    categoryBg: '#F0F9FF',
    title: 'Building a Zero-Downtime Deployment Pipeline with AWS and GitHub Actions',
    excerpt: 'A step-by-step guide to setting up blue-green deployments, automated rollbacks, and health checks — ensuring your app stays live even during production pushes.',
    author: 'QuBright Tech Team',
    date: 'August 10, 2026',
    readTime: '12 min read',
    emoji: '☁️',
    tags: ['AWS', 'CI/CD', 'DevOps'],
    featured: false,
  },
  {
    id: 5,
    category: 'Product Design',
    categoryColor: '#8B5CF6',
    categoryBg: '#F5F3FF',
    title: 'Design Systems: Why Every Growing Team Needs One',
    excerpt: 'Inconsistent UI, duplicated components, and slow onboarding — these are signs you need a design system. Here\'s how to build one that actually gets used.',
    author: 'QuBright Design Team',
    date: 'August 7, 2026',
    readTime: '6 min read',
    emoji: '🎨',
    tags: ['Design', 'UI/UX', 'Productivity'],
    featured: false,
  },
  {
    id: 6,
    category: 'Startup',
    categoryColor: '#F59E0B',
    categoryBg: '#FFFBEB',
    title: 'From MVP to Scale: 10 Technical Decisions That Will Define Your Startup',
    excerpt: 'The choices you make when building your MVP can haunt you for years — or set you up for hypergrowth. Here are the architectural and technology decisions that matter most.',
    author: 'QuBright Tech Team',
    date: 'August 3, 2026',
    readTime: '9 min read',
    emoji: '🚀',
    tags: ['Startup', 'Architecture', 'MVP'],
    featured: false,
  },
];

const categories = ['All', 'Artificial Intelligence', 'Mobile Development', 'EdTech', 'Cloud & DevOps', 'Product Design', 'Startup'];

function FeaturedCard({ post }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ y: 40, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.8 }}
      className="bg-gradient-to-br from-slate-900 to-blue-950 rounded-3xl overflow-hidden shadow-royal group cursor-pointer hover:-translate-y-1 transition-all duration-300"
    >
      <div className="p-8 md:p-12">
        <div className="flex items-center gap-3 mb-6">
          <span
            className="px-3 py-1 rounded-full text-xs font-space font-bold"
            style={{ color: post.categoryColor, background: post.categoryBg }}
          >
            ✦ Featured
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-space font-semibold text-blue-300 bg-white/10">
            {post.category}
          </span>
        </div>

        <div className="text-7xl mb-6">{post.emoji}</div>

        <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-white mb-4 leading-tight group-hover:text-blue-200 transition-colors duration-300">
          {post.title}
        </h2>
        <p className="font-inter text-blue-200 text-base leading-relaxed mb-8 max-w-2xl">
          {post.excerpt}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5 text-blue-300 text-sm font-inter">
            <span className="flex items-center gap-1.5"><User size={14} />{post.author}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} />{post.readTime}</span>
            <span className="flex items-center gap-1.5"><Tag size={14} />{post.date}</span>
          </div>
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-brand-blue font-space font-semibold text-sm hover:bg-blue-50 transition-all duration-200">
            Read Article <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function BlogCard({ post, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ y: 40, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-card hover:shadow-royal transition-all duration-300 hover:-translate-y-1 group cursor-pointer flex flex-col"
    >
      {/* Card Header */}
      <div
        className="p-6 rounded-t-2xl flex items-center justify-between"
        style={{ background: `linear-gradient(135deg, ${post.categoryBg} 0%, white 100%)` }}
      >
        <span
          className="px-3 py-1 rounded-full text-xs font-space font-bold"
          style={{ color: post.categoryColor, background: post.categoryBg, border: `1px solid ${post.categoryColor}30` }}
        >
          {post.category}
        </span>
        <span className="text-4xl">{post.emoji}</span>
      </div>

      {/* Card Body */}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-syne font-bold text-xl text-slate-900 mb-3 leading-snug group-hover:text-brand-blue transition-colors duration-200">
          {post.title}
        </h3>
        <p className="font-inter text-slate-500 text-sm leading-relaxed mb-4 flex-1">
          {post.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {post.tags.map((tag, i) => (
            <span key={i} className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-space font-medium">
              #{tag}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="text-xs font-inter text-slate-400 flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock size={12} />{post.readTime}</span>
            <span>{post.date}</span>
          </div>
          <button
            className="flex items-center gap-1 text-xs font-space font-semibold group-hover:gap-2 transition-all duration-200"
            style={{ color: post.categoryColor }}
          >
            Read <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');

  const featured = blogPosts.find(p => p.featured);
  const filtered = blogPosts
    .filter(p => !p.featured)
    .filter(p => activeCategory === 'All' || p.category === activeCategory);

  return (
    <div className="min-h-screen bg-brand-light">
      <SEO
        title="Blog"
        description="Insights, tutorials, and deep dives on AI, mobile development, cloud, EdTech, and startup technology from the QuBright engineering team."
        url="https://qubright.web.app/blog"
      />
      <Navbar forceOpaque />

      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-orange/10 blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-blue/20 blur-[80px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="section-label mb-6 inline-block">Our Blog</span>
            <h1 className="font-syne font-extrabold text-5xl md:text-6xl text-white mb-5 leading-tight">
              Ideas, Insights &<span className="block gradient-text">Deep Dives</span>
            </h1>
            <p className="font-inter text-blue-200 text-xl leading-relaxed">
              Engineering wisdom, product thinking, and industry perspectives from the QuBright team.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="#F8FAFC" />
          </svg>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <FeaturedCard post={featured} />
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-6 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-space font-semibold transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-brand-blue text-white shadow-md'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-brand-blue hover:text-brand-blue'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-10 pb-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post, i) => (
              <BlogCard key={post.id} post={post} index={i} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-5xl mb-4">📭</p>
              <p className="font-syne font-bold text-slate-700 text-xl">No posts in this category yet.</p>
              <p className="font-inter text-slate-500 mt-2">Check back soon — we publish weekly!</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="text-5xl mb-5">✉️</div>
            <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-slate-900 mb-4">
              Stay in the <span className="gradient-text">Loop</span>
            </h2>
            <p className="font-inter text-slate-500 text-lg mb-8">
              Get the latest articles, product updates, and tech insights delivered straight to your inbox. No spam, ever.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="you@company.com"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 font-inter text-sm text-slate-800 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 transition-all"
              />
              <button className="btn-primary whitespace-nowrap">Subscribe →</button>
            </div>
            <p className="font-space text-slate-400 text-xs mt-4">We publish every week. Unsubscribe anytime.</p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
